import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeCustomizationContext = createContext();

const DEFAULT_THEME_CONFIG = {
  primaryColor: '#02518C',
  secondaryColor: '#288FC6',
  mode: 'light',
};

export const ThemeCustomizationProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('userThemeConfig');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migrar configs viejas con colores genéricos no-marca
        if (parsed.primaryColor === '#2563EB' || parsed.primaryColor === '#6366F1') {
          localStorage.removeItem('userThemeConfig');
          return DEFAULT_THEME_CONFIG;
        }
        return parsed;
      }
      return DEFAULT_THEME_CONFIG;
    } catch {
      return DEFAULT_THEME_CONFIG;
    }
  });

  useEffect(() => {
    localStorage.setItem('userThemeConfig', JSON.stringify(themeConfig));
  }, [themeConfig]);

  const updateThemeConfig = (newConfig) => {
    setThemeConfig((prev) => ({ ...prev, ...newConfig }));
  };

  const resetTheme = () => {
    setThemeConfig(DEFAULT_THEME_CONFIG);
    localStorage.removeItem('userThemeConfig');
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeConfig.mode,
          primary: {
            main: themeConfig.primaryColor,
            light: '#288FC6',
            dark: '#153250',
            contrastText: '#ffffff',
          },
          secondary: {
            main: themeConfig.secondaryColor,
            light: '#5BAFD8',
            dark: '#02518C',
            contrastText: '#ffffff',
          },
          background: {
            default: themeConfig.mode === 'dark' ? '#0F172A' : '#F8FAFC',
            paper: themeConfig.mode === 'dark' ? '#1E293B' : '#ffffff',
          },
          text: {
            primary: themeConfig.mode === 'dark' ? '#F1F5F9' : '#1E293B',
            secondary: themeConfig.mode === 'dark' ? '#94A3B8' : '#64748B',
          },
          divider: themeConfig.mode === 'dark' ? '#334155' : '#E2E8F0',
          info: { main: '#0EA5E9', light: '#38BDF8', dark: '#0284C7', contrastText: '#ffffff' },
          success: { main: '#10B981', light: '#34D399', dark: '#059669', contrastText: '#ffffff' },
          warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706', contrastText: '#ffffff' },
          error: { main: '#EF4444', light: '#FCA5A5', dark: '#DC2626', contrastText: '#ffffff' },
          grey: {
            50: '#F8FAFC', 100: '#F1F5F9', 200: '#E2E8F0', 300: '#CBD5E1',
            400: '#94A3B8', 500: '#64748B', 600: '#475569', 700: '#334155',
            800: '#1E293B', 900: '#0F172A',
          },
        },
        typography: {
          fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          h1: { fontWeight: 700, letterSpacing: '-0.02em' },
          h2: { fontWeight: 700, letterSpacing: '-0.01em' },
          h3: { fontWeight: 600, letterSpacing: '-0.01em' },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          body1: { lineHeight: 1.6 },
          body2: { lineHeight: 1.6 },
          subtitle1: { fontWeight: 500 },
          subtitle2: { fontWeight: 600 },
          button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
        },
        shape: { borderRadius: 10 },
        shadows: [
          'none',
          '0 1px 2px 0 rgba(0,0,0,0.05)',
          '0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
          '0 4px 16px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.05)',
          '0 8px 24px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.05)',
          '0 20px 25px -5px rgba(0,0,0,0.06), 0 8px 10px -6px rgba(0,0,0,0.04)',
          '0 25px 50px -12px rgba(0,0,0,0.12)',
          ...Array(18).fill('none'),
        ],
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: '#F8FAFC',
                transition: 'background-color 0.2s ease, color 0.2s ease',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 10,
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: '8px 20px',
                letterSpacing: '0.01em',
                transition: 'all 0.2s ease',
                boxShadow: 'none',
              },
              contained: {
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 4px 16px rgba(2,81,140,0.22)', transform: 'translateY(-1px)' },
                '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              },
              outlined: {
                borderWidth: '1.5px',
                borderColor: 'rgba(2,81,140,0.35)',
                '&:hover': { borderWidth: '1.5px', borderColor: '#02518C', backgroundColor: 'rgba(2,81,140,0.04)' },
              },
              text: { '&:hover': { backgroundColor: 'rgba(2,81,140,0.06)' } },
              sizeSmall: { padding: '5px 14px', fontSize: '0.8125rem', borderRadius: 8 },
              sizeLarge: { padding: '12px 28px', fontSize: '1rem', borderRadius: 12 },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 14,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                backgroundImage: 'none',
              },
            },
          },
          MuiCardContent: {
            styleOverrides: { root: { '&:last-child': { paddingBottom: 20 } } },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                color: '#1E293B',
                boxShadow: 'none',
                borderBottom: '1px solid #E2E8F0',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: { backgroundColor: '#153250', borderRight: 'none', boxShadow: 'none' },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: { borderRadius: 8, transition: 'all 0.15s ease' },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { fontWeight: 500, fontSize: '0.8rem', borderRadius: 6 },
              colorPrimary: { backgroundColor: 'rgba(2,81,140,0.1)', color: '#02518C', fontWeight: 600 },
              colorSuccess: { backgroundColor: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 600 },
              colorWarning: { backgroundColor: 'rgba(245,158,11,0.1)', color: '#D97706', fontWeight: 600 },
              colorError: { backgroundColor: 'rgba(239,68,68,0.1)', color: '#DC2626', fontWeight: 600 },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto',
              },
            },
          },
          MuiTable: {
            defaultProps: {
              stickyHeader: true,
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-head': {
                  backgroundColor: '#F8FAFC !important',
                  fontWeight: 600,
                  color: '#475569',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  borderBottom: '1px solid #E2E8F0',
                },
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:hover': { backgroundColor: '#F8FAFC' },
                '&:last-child .MuiTableCell-body': { borderBottom: 0 },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: { root: { borderColor: '#E2E8F0', fontSize: '0.875rem' } },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                backgroundColor: '#F5F7FA',
                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: '1.5px' },
                '&:hover': { backgroundColor: '#EDF0F5' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&.Mui-focused': { backgroundColor: '#ffffff', boxShadow: '0 0 0 3px rgba(40,143,198,0.12)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#288FC6', borderWidth: '1.5px' },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: { color: '#64748B', fontWeight: 500, '&.Mui-focused': { color: '#288FC6' } },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: { borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)' },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: { fontWeight: 700, fontSize: '1.125rem', paddingBottom: 8 },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: 'none' },
              elevation1: { boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' },
              elevation2: { boxShadow: '0 4px 16px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.05)' },
              elevation3: { boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.05)' },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: { borderRadius: 8, fontWeight: 500, fontSize: '0.875rem' },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { borderRadius: 6, fontSize: '0.75rem', fontWeight: 500 },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: { borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', backgroundImage: 'none' },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                mx: 0.5,
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: 'rgba(2,81,140,0.06)' },
                '&.Mui-selected': { backgroundColor: 'rgba(2,81,140,0.1)', fontWeight: 600 },
              },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              root: { borderRadius: 4, height: 6 },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: { height: 2.5, borderRadius: '2px 2px 0 0' },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                '&.Mui-selected': { fontWeight: 700 },
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              root: { width: 46, height: 26, padding: 0 },
              switchBase: {
                padding: 1,
                '&.Mui-checked': { transform: 'translateX(20px)' },
              },
              thumb: { width: 24, height: 24, boxShadow: 'none' },
              track: { borderRadius: 13, opacity: 1, backgroundColor: '#E2E8F0' },
            },
          },
        },
      }),
    [themeConfig]
  );

  const value = { themeConfig, updateThemeConfig, resetTheme };

  return (
    <ThemeCustomizationContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeCustomizationContext.Provider>
  );
};

export const useThemeCustomization = () => {
  const context = useContext(ThemeCustomizationContext);
  if (!context) {
    throw new Error('useThemeCustomization must be used within ThemeCustomizationProvider');
  }
  return context;
};
